import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import {
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Alert,
} from "react-bootstrap";
import {
  FaSignOutAlt,
  FaUserCircle,
  FaCalendarAlt,
  FaChartLine,
  FaLock,
  FaSyncAlt,
} from "react-icons/fa";

import { auth, db } from "../firebase";
import CustomerStockCards from "../components/CustomerStockCards";

function CustomerDashboard() {
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [stockCalls, setStockCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const normalizeEmail = (value) => {
    return String(value || "").trim().toLowerCase();
  };

  const calculateDaysLeft = (expiryDate) => {
    if (!expiryDate) return 0;

    const today = new Date();
    const expiry = new Date(`${expiryDate}T23:59:59`);

    const difference = expiry - today;

    return Math.ceil(
      difference / (1000 * 60 * 60 * 24)
    );
  };

  const isCustomerActive = (customerData) => {
    if (!customerData) return false;

    const daysLeft = calculateDaysLeft(
      customerData.expiryDate
    );

    return (
      customerData.status === "Active" &&
      daysLeft >= 0
    );
  };

  const convertToMilliseconds = (value) => {
    if (!value) return null;

    // Normal JavaScript timestamp saved using Date.now().
    if (typeof value === "number") {
      return value;
    }

    // Firestore Timestamp.
    if (typeof value?.toMillis === "function") {
      return value.toMillis();
    }

    // Firestore Timestamp data containing seconds.
    if (typeof value?.seconds === "number") {
      return value.seconds * 1000;
    }

    // Date or date string.
    const convertedDate = new Date(value);

    if (!Number.isNaN(convertedDate.getTime())) {
      return convertedDate.getTime();
    }

    return null;
  };

  const getCustomerJoiningTimestamp = (
    customerData
  ) => {
    return (
      convertToMilliseconds(customerData.createdAt) ||
      convertToMilliseconds(customerData.joinedAt) ||
      convertToMilliseconds(customerData.joinDate) ||
      null
    );
  };

  const getCallCreatedTimestamp = (call) => {
    return (
      convertToMilliseconds(call.createdAt) ||
      convertToMilliseconds(call.recommendationDate) ||
      null
    );
  };

  const customerCanViewCall = (
    call,
    customerData,
    allowedCallAccess
  ) => {
    // Customer must have access to this call type.
    if (!allowedCallAccess.includes(call.callType)) {
      return false;
    }

    // Closed calls continue following the existing dashboard logic.
    // Calls without status are treated normally for older records.
    if (
      call.status &&
      call.status !== "Active" &&
      call.status !== "Closed"
    ) {
      return false;
    }

    // Check whether this customer was excluded from this call.
    const excludedCustomerIds = Array.isArray(
      call.excludedCustomerIds
    )
      ? call.excludedCustomerIds
      : [];

    if (
      excludedCustomerIds.includes(customerData.id)
    ) {
      return false;
    }

    /*
      New-customer rule:

      Customers with createdAt/joinedAt/joinDate only see calls
      uploaded on or after their joining time.

      Existing customers whose old records do not yet have a
      joining timestamp continue seeing earlier calls, preventing
      any unexpected change to current customers.
    */
    const customerJoiningTimestamp =
      getCustomerJoiningTimestamp(customerData);

    if (customerJoiningTimestamp) {
      const callCreatedTimestamp =
        getCallCreatedTimestamp(call);

      if (
        callCreatedTimestamp &&
        callCreatedTimestamp <
          customerJoiningTimestamp
      ) {
        return false;
      }
    }

    return true;
  };

  const loadCustomerData = async (
    loggedInEmail
  ) => {
    try {
      const cleanEmail =
        normalizeEmail(loggedInEmail);

      const customerRef = doc(
        db,
        "customers",
        cleanEmail
      );

      const customerSnap = await getDoc(
        customerRef
      );

      if (!customerSnap.exists()) {
        localStorage.clear();
        await signOut(auth);
        navigate("/customer-login");
        return;
      }

      const customerData = {
        id: customerSnap.id,
        ...customerSnap.data(),
      };

      setCustomer(customerData);

      localStorage.setItem(
        "customerEmail",
        customerData.email || cleanEmail
      );

      localStorage.setItem(
        "customerPhone",
        customerData.phone || ""
      );

      localStorage.setItem(
        "customerName",
        customerData.name || ""
      );

      if (isCustomerActive(customerData)) {
        const callsSnapshot = await getDocs(
          collection(db, "stockCalls")
        );

        const callsList =
          callsSnapshot.docs.map(
            (callDocument) => ({
              id: callDocument.id,
              ...callDocument.data(),
            })
          );

        const allowedCallAccess =
          Array.isArray(customerData.callAccess) &&
          customerData.callAccess.length > 0
            ? customerData.callAccess
            : [
                "Long Term",
                "Mid Term",
                "Short Term",
              ];

        const filteredCalls = callsList
          .filter((call) =>
            customerCanViewCall(
              call,
              customerData,
              allowedCallAccess
            )
          )
          .sort((firstCall, secondCall) => {
            const firstTimestamp =
              getCallCreatedTimestamp(firstCall) || 0;

            const secondTimestamp =
              getCallCreatedTimestamp(secondCall) || 0;

            return secondTimestamp - firstTimestamp;
          });

        setStockCalls(filteredCalls);
      } else {
        setStockCalls([]);
      }

      setLoading(false);
    } catch (error) {
      console.error(
        "Unable to load customer dashboard:",
        error
      );

      alert(
        "Unable to load customer dashboard. Please try again."
      );

      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe =
      onAuthStateChanged(
        auth,
        async (user) => {
          if (!user || !user.email) {
            localStorage.clear();
            navigate("/customer-login");
            return;
          }

          await loadCustomerData(user.email);
        }
      );

    return () => unsubscribe();
  }, []);

  const refreshDashboard = async () => {
    try {
      const currentUser = auth.currentUser;

      if (
        !currentUser ||
        !currentUser.email
      ) {
        localStorage.clear();
        navigate("/customer-login");
        return;
      }

      setRefreshing(true);

      await loadCustomerData(
        currentUser.email
      );
    } catch (error) {
      console.error(error);

      alert(
        "Unable to refresh dashboard. Please try again."
      );
    } finally {
      setRefreshing(false);
    }
  };

  const logout = async () => {
    await signOut(auth);
    localStorage.clear();
    navigate("/customer-login");
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <h4>
          Loading customer dashboard...
        </h4>
      </Container>
    );
  }

  if (!customer) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          Customer record not found. Please
          contact admin.
        </Alert>
      </Container>
    );
  }

  const daysLeft = calculateDaysLeft(
    customer.expiryDate
  );

  const active =
    isCustomerActive(customer);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f1f5f9",
      }}
    >
      <div
        style={{
          background:
            "linear-gradient(135deg, #0f172a, #14532d)",
          color: "white",
          padding: "25px 0",
        }}
      >
        <Container>
          <Row className="align-items-center">
            <Col xs={12} md={8}>
              <h2 className="fw-bold mb-1">
                <FaChartLine className="me-2" />
                Trading Wealth Dashboard
              </h2>

              <p className="mb-0">
                Welcome back,{" "}
                {customer.name || "Customer"}
              </p>
            </Col>

            <Col
              xs={12}
              md={4}
              className="text-md-end mt-3 mt-md-0"
            >
              <Button
                variant="outline-light"
                className="me-2 mb-2"
                onClick={refreshDashboard}
                disabled={refreshing}
              >
                <FaSyncAlt className="me-2" />

                {refreshing
                  ? "Refreshing..."
                  : "Refresh"}
              </Button>

              <Button
                variant="outline-light"
                className="mb-2"
                onClick={logout}
              >
                <FaSignOutAlt className="me-2" />
                Logout
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-4">
        <Row className="mb-4">
          <Col md={4} className="mb-3">
            <Card className="shadow border-0 h-100">
              <Card.Body>
                <h5 className="fw-bold">
                  <FaUserCircle className="me-2 text-success" />
                  Customer Profile
                </h5>

                <p className="mb-1">
                  <strong>Name:</strong>{" "}
                  {customer.name}
                </p>

                <p className="mb-1">
                  <strong>Email:</strong>{" "}
                  {customer.email}
                </p>

                <p className="mb-1">
                  <strong>Phone:</strong>{" "}
                  {customer.phone ||
                    "Not added"}
                </p>

                <p className="mb-0">
                  <strong>Plan:</strong>{" "}
                  {customer.plan}
                </p>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4} className="mb-3">
            <Card className="shadow border-0 h-100">
              <Card.Body>
                <h5 className="fw-bold">
                  <FaCalendarAlt className="me-2 text-primary" />
                  Subscription
                </h5>

                <p className="mb-1">
                  <strong>Status:</strong>{" "}
                  <Badge
                    bg={
                      active
                        ? "success"
                        : "danger"
                    }
                  >
                    {active
                      ? "Active"
                      : "Expired"}
                  </Badge>
                </p>

                <p className="mb-1">
                  <strong>
                    Expiry Date:
                  </strong>{" "}
                  {customer.expiryDate}
                </p>

                <p className="mb-0">
                  <strong>
                    Days Left:
                  </strong>{" "}
                  <Badge
                    bg={
                      daysLeft >= 0
                        ? "success"
                        : "danger"
                    }
                  >
                    {daysLeft >= 0
                      ? `${daysLeft} Days Left`
                      : "Expired"}
                  </Badge>
                </p>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4} className="mb-3">
            <Card className="shadow border-0 h-100">
              <Card.Body>
                <h5 className="fw-bold">
                  <FaLock className="me-2 text-warning" />
                  Call Access
                </h5>

                {(customer.callAccess ||
                  []).map((access) => (
                  <Badge
                    bg="info"
                    text="dark"
                    className="me-2 mb-2"
                    key={access}
                  >
                    {access}
                  </Badge>
                ))}

                {(!customer.callAccess ||
                  customer.callAccess
                    .length === 0) && (
                  <p className="text-muted mb-0">
                    No call access assigned.
                  </p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {!active ? (
          <Alert
            variant="danger"
            className="shadow-sm"
          >
            <h5 className="fw-bold">
              Subscription Expired
            </h5>

            <p className="mb-0">
              Your Trading Wealth
              subscription has expired. Please
              contact admin to renew your plan
              and continue viewing stock calls.
            </p>
          </Alert>
        ) : (
          <CustomerStockCards
            stockCalls={stockCalls}
            customer={customer}
          />
        )}
      </Container>
    </div>
  );
}

export default CustomerDashboard;